import { apiFetch } from "./client";
import {
  canonicalStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "./stocky_public_ids";

export const PLAN_PREVIEW_COLUMNS = {
  report_profit_inputs_30d: [
    "product",
    "sku",
    "quantity",
    "revenue",
    "cost",
    "profit",
    "margin",
  ],
  report_supplier_inputs_30d: [
    "supplier",
    "purchase_order",
    "ordered_on",
    "expected_on",
    "item_count",
    "ordered_amount",
    "status",
  ],
  inventory_valuation: ["product", "sku", "available", "unit_cost", "line_value"],
  inventory_abc_30d: [
    "product",
    "sku",
    "quantity_sold",
    "sales_value",
    "cumulative_share",
    "rank",
  ],
  inventory_dead_stock_90d: [
    "product",
    "sku",
    "available",
    "unit_cost",
    "tied_value",
    "last_sale",
  ],
  bom_kits: ["kit", "sku", "component_count", "buildable_quantity"],
  reorder_policies: [
    "product",
    "sku",
    "supplier",
    "available",
    "incoming",
    "ordered_open",
    "reorder_point",
    "lead_time",
  ],
  cashflow_inputs: [
    "product",
    "sku",
    "supplier",
    "reorder_point",
    "lead_time",
    "unit_cost",
  ],
  stocktake_candidates: ["product", "sku", "expected_qty", "counted_qty", "variance"],
  transfer_candidates: ["product", "sku", "available", "transfer_qty"],
  landed_cost: [
    "product",
    "sku",
    "ordered_qty",
    "unit_price",
    "line_amount",
    "allocated_amount",
    "landed_line_amount",
    "landed_unit_cost",
  ],
} as const;

export type PlanPreviewDataset = keyof typeof PLAN_PREVIEW_COLUMNS;
export type PlanPreviewCellTier =
  | "empty"
  | "visible"
  | "masked"
  | "partial_1"
  | "partial_2";

export interface PlanPreviewCell {
  tier: PlanPreviewCellTier;
  displayText: string | null;
}

export interface PlanPreviewRow {
  cells: PlanPreviewCell[];
}

/** Validated client value. `dataset` is bound locally from the requested path. */
export interface PlanPreview {
  dataset: PlanPreviewDataset;
  count: number;
  columnKeys: readonly string[];
  rows: PlanPreviewRow[];
}

const PARTIAL_COLUMN: Record<PlanPreviewDataset, string> = {
  report_profit_inputs_30d: "product",
  report_supplier_inputs_30d: "supplier",
  inventory_valuation: "product",
  inventory_abc_30d: "product",
  inventory_dead_stock_90d: "product",
  bom_kits: "kit",
  reorder_policies: "product",
  cashflow_inputs: "product",
  stocktake_candidates: "product",
  transfer_candidates: "product",
  landed_cost: "product",
};

const LEGACY_CELL_TIERS = new Set<PlanPreviewCellTier>([
  "empty",
  "masked",
  "partial_1",
  "partial_2",
]);
const OTHER_CATEGORY = /\p{C}/u;
const SAFE_ZWJ_GRAPHEME = /^\p{Extended_Pictographic}(?:\ufe0f|\p{Emoji_Modifier})?(?:\u200d\p{Extended_Pictographic}(?:\ufe0f|\p{Emoji_Modifier})?)+$/u;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function graphemes(value: string): string[] {
  return Array.from(
    new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(value),
    ({ segment }) => segment,
  );
}

function hasForbiddenDisplayCharacters(value: string): boolean {
  return graphemes(value).some(
    (grapheme) =>
      OTHER_CATEGORY.test(grapheme) && !SAFE_ZWJ_GRAPHEME.test(grapheme),
  );
}

function invalidPreview(): never {
  throw new Error("plan preview response did not match the safe schema");
}

function parseCell(
  raw: unknown,
  {
    dataset,
    columnKey,
    rowIndex,
  }: {
    dataset: PlanPreviewDataset;
    columnKey: string;
    rowIndex: number;
  },
): PlanPreviewCell {
  if (!isRecord(raw) || !hasExactKeys(raw, ["tier", "display_text"])) invalidPreview();
  const tier = raw.tier;
  const displayText = raw.display_text;
  if (
    typeof tier !== "string" ||
    !LEGACY_CELL_TIERS.has(tier as PlanPreviewCellTier)
  ) {
    invalidPreview();
  }

  if (tier === "empty" || tier === "masked") {
    if (displayText !== null) invalidPreview();
    return { tier, displayText: null } as PlanPreviewCell;
  }

  if (columnKey !== PARTIAL_COLUMN[dataset] || typeof displayText !== "string") {
    invalidPreview();
  }
  if (
    displayText.trim() !== displayText ||
    displayText.normalize("NFKC") !== displayText ||
    displayText === "" ||
    hasForbiddenDisplayCharacters(displayText)
  ) {
    invalidPreview();
  }

  const expectedTier = rowIndex === 0 ? "partial_2" : rowIndex === 1 ? "partial_1" : null;
  if (tier !== expectedTier) invalidPreview();
  const expectedGraphemes = tier === "partial_2" ? 2 : 1;
  if (graphemes(displayText).length !== expectedGraphemes) invalidPreview();
  return { tier, displayText } as PlanPreviewCell;
}

function parseLegacyPreview(
  dataset: PlanPreviewDataset,
  raw: Record<string, unknown>,
): PlanPreview {
  if (
    !hasExactKeys(raw, ["schema_version", "count", "column_keys", "rows"]) ||
    raw.schema_version !== 1 ||
    !Number.isSafeInteger(raw.count) ||
    (raw.count as number) < 0 ||
    !Array.isArray(raw.column_keys) ||
    !Array.isArray(raw.rows)
  ) {
    invalidPreview();
  }

  const expectedColumns = PLAN_PREVIEW_COLUMNS[dataset];
  if (
    raw.column_keys.length !== expectedColumns.length ||
    !raw.column_keys.every((key, index) => key === expectedColumns[index]) ||
    raw.rows.length !== Math.min(raw.count as number, 5)
  ) {
    invalidPreview();
  }

  const rows = raw.rows.map((row, rowIndex): PlanPreviewRow => {
    if (!isRecord(row) || !hasExactKeys(row, ["cells"]) || !Array.isArray(row.cells)) {
      invalidPreview();
    }
    if (row.cells.length !== expectedColumns.length) invalidPreview();
    return {
      cells: row.cells.map((cell, columnIndex) =>
        parseCell(cell, {
          dataset,
          columnKey: expectedColumns[columnIndex] as string,
          rowIndex,
        }),
      ),
    };
  });

  return {
    dataset,
    count: raw.count as number,
    columnKeys: expectedColumns,
    rows,
  };
}

function parseVisiblePreview(
  dataset: PlanPreviewDataset,
  raw: Record<string, unknown>,
): PlanPreview {
  if (
    !hasExactKeys(raw, ["schema_version", "count", "column_keys", "rows"]) ||
    raw.schema_version !== 2 ||
    !Number.isSafeInteger(raw.count) ||
    (raw.count as number) < 0 ||
    !Array.isArray(raw.column_keys) ||
    !Array.isArray(raw.rows)
  ) {
    invalidPreview();
  }

  const expectedColumns = PLAN_PREVIEW_COLUMNS[dataset];
  if (
    raw.column_keys.length !== expectedColumns.length ||
    !raw.column_keys.every((key, index) => key === expectedColumns[index]) ||
    raw.rows.length !== Math.min(raw.count as number, 1)
  ) {
    invalidPreview();
  }

  const rows = raw.rows.map((row): PlanPreviewRow => {
    if (!isRecord(row) || !hasExactKeys(row, ["cells"]) || !Array.isArray(row.cells)) {
      invalidPreview();
    }
    if (row.cells.length !== expectedColumns.length) invalidPreview();
    return {
      cells: row.cells.map((cell): PlanPreviewCell => {
        if (!isRecord(cell) || !hasExactKeys(cell, ["tier", "display_text"])) {
          invalidPreview();
        }
        if (cell.tier === "empty") {
          if (cell.display_text !== null) invalidPreview();
          return { tier: "empty", displayText: null };
        }
        if (cell.tier !== "visible" || typeof cell.display_text !== "string") {
          invalidPreview();
        }
        if (
          cell.display_text.length > 2_048 ||
          cell.display_text.trim() !== cell.display_text ||
          cell.display_text.normalize("NFKC") !== cell.display_text ||
          hasForbiddenDisplayCharacters(cell.display_text)
        ) {
          invalidPreview();
        }
        return { tier: "visible", displayText: cell.display_text };
      }),
    };
  });

  return {
    dataset,
    count: raw.count as number,
    columnKeys: expectedColumns,
    rows,
  };
}

function parsePreview(dataset: PlanPreviewDataset, raw: unknown): PlanPreview {
  if (!isRecord(raw)) invalidPreview();
  if (raw.schema_version === 1) return parseLegacyPreview(dataset, raw);
  if (raw.schema_version === 2) return parseVisiblePreview(dataset, raw);
  return invalidPreview();
}

export async function getPlanPreview(dataset: Exclude<PlanPreviewDataset, "landed_cost">) {
  const raw = await apiFetch<unknown>(`/api/plan-previews-v2/${dataset}`);
  return parsePreview(dataset, raw);
}

export async function getLandedCostPlanPreview(poId: StockyPublicBigIntId) {
  const canonicalPoId = canonicalStockyPublicBigIntId(poId);
  const raw = await apiFetch<unknown>(
    `/api/purchase-orders/placed/${canonicalPoId}/plan-preview-v2/landed-cost`,
  );
  return parsePreview("landed_cost", raw);
}

export const planPreviewQueryKey = (
  dataset: PlanPreviewDataset,
  context?: StockyPublicBigIntId,
) =>
  context === undefined
    ? (["plan-preview", dataset] as const)
    : (["plan-preview", dataset, context] as const);
